import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import PlayPause from "./components/PlayPause";
import JumpToNextTimestamp from "./components/JumpToNextTimestamp";
import JumpToPreviousTimestamp from "./components/JumpToPreviousTimestamp";
import EnterExitFullscreen from "./components/EnterExitFullscreen";
import VideoProgressBar from "./components/progress-bar/VideoProgressBar";
import TrackDetails from "./components/TrackDetails";
import TimeDetails from "./components/TimeDetails";
import DetachVideo from "./components/DetachVideo";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import HighlightSettings from "./components/HighlightSettings";
import { HighlightDetectedDamagesFilter } from "./components/HighlightDetectedDamagesFilter";
import { VideoAnalysisService } from "../../services/inspection/VideoAnalysisService";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import { Flags } from "../../generated/FeatureFlags";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";

interface VideoControlsProps {
  videoAnalysis?: VideoAnalysisService;
  filteredSegmentations?: Segmentation[];
}

export default observer(function VideoControls(props: VideoControlsProps) {
  const { isVideoPlaying, isPictureInPicture, isFullscreen, enterFullscreen, exitFullscreen } =
    videoPlayerService;
  const { videoAnalysis, filteredSegmentations } = props;

  const shouldDisplayHighlightDetectionStatus = useMemo(
    () =>
      featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) &&
      (videoAnalysis?.segmentationInProgress || videoAnalysis?.segmentationFailed),
    [videoAnalysis?.segmentationFailed, videoAnalysis?.segmentationInProgress]
  );

  const shouldDisplayHighlightDetectionSettings = useMemo(
    () =>
      featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) &&
      videoAnalysis?.segmentationWasSuccessful &&
      videoAnalysis?.analysisWasSuccessful &&
      !videoAnalysis?.analysisInProgress,
    [
      videoAnalysis?.analysisInProgress,
      videoAnalysis?.analysisWasSuccessful,
      videoAnalysis?.segmentationWasSuccessful,
    ]
  );

  return (
    <section className="video-controls">
      <VideoProgressBar
        videoAnalysis={videoAnalysis}
        filteredSegmentations={filteredSegmentations}
      />
      <div className="video-buttons">
        <div className="position-left">
          <PlayPause
            tooltipTextKey={isVideoPlaying ? "VideoControls.pause" : "VideoControls.play"}
          />
          <TimeDetails />
        </div>
        <div className="position-center">
          <JumpToPreviousTimestamp tooltipTextKey="VideoControls.previous" />
          <TrackDetails />
          <JumpToNextTimestamp tooltipTextKey="VideoControls.next" />
        </div>
        <div className="position-right">
          {shouldDisplayHighlightDetectionStatus && (
            <HighlightDetectedDamagesFilter videoAnalysis={videoAnalysis} />
          )}
          {shouldDisplayHighlightDetectionSettings && (
            <HighlightSettings tooltipTextKey="VideoAnalysis.video-settings.tooltip" />
          )}
          <DetachVideo
            tooltipTextKey={
              isPictureInPicture ? "VideoControls.exit-detach" : "VideoControls.detach"
            }
          />
          <EnterExitFullscreen
            enter={enterFullscreen}
            exit={exitFullscreen}
            isFullscreen={isFullscreen}
            tooltipTextKey={
              isFullscreen ? "VideoControls.exit-fullscreen" : "VideoControls.enter-fullscreen"
            }
          />
        </div>
      </div>
    </section>
  );
});
